import type { ProtocolDef } from "./definitions";

/**
 * Base-goal protocols — extend the original 4 onboarding goals
 * (skin, weight, muscle, antiaging) with the same protocol mechanics
 * (guided photo cadence, AI observation, opt-in extension) used by the
 * specialised protocols. Tier = "core" → included with Premium.
 *
 * Cadence "adapted per goal":
 *  - skin       → every ~3 days for 30 days (fast biological signal)
 *  - antiaging  → weekly for 30 days (slow signal, monthly extension)
 *  - weight     → weekly for 30 days
 *  - muscle     → weekly for 30 days
 *
 * All four expose an opt-in extension that doubles the horizon to ~60–90 days,
 * so a monthly subscription continues to produce useful comparisons.
 */

function s(
  key: string,
  dayOffset: number,
  title: Record<"fr"|"en"|"nl", string>,
  checklist: Record<"fr"|"en"|"nl", string[]>,
  bodyArea: ProtocolDef["steps"][number]["bodyArea"] = "face",
  referenceHint?: Record<"fr"|"en"|"nl", string>,
) {
  return { key, dayOffset, selfieExpected: true, title, checklist, bodyArea, referenceHint };
}

// ----------------------------- SKIN -----------------------------
const SKIN: ProtocolDef = {
  goal: "skin",
  tier: "core",
  title: { fr: "Peau & Éclat · 30 jours", en: "Skin & Glow · 30 days", nl: "Huid & Glans · 30 dagen" },
  subtitle: {
    fr: "Une photo tous les 3 jours pendant 30 j — texture, hydratation, éclat.",
    en: "One photo every 3 days for 30 days — texture, hydration, glow.",
    nl: "Eén foto om de 3 dagen, 30 dagen lang — textuur, hydratatie, glans.",
  },
  aiContext: {
    fr: "Suivi cosmétique non médical : peau & éclat. Décris ce qui est VISIBLE (texture, brillance, rougeurs, pores). Pas de nom de pathologie. Conseils doux : hydratation, SPF, sommeil, hygiène. Red flags : poussée brutale, douleur, lésions qui s'étendent → dermatologue.",
    en: "Non-medical skin & glow tracking. Describe what's VISIBLE (texture, shine, redness, pores). No disease names. Gentle advice: hydration, SPF, sleep, hygiene. Red flags: sudden flare, pain, spreading lesions → dermatologist.",
    nl: "Niet-medische opvolging huid & glans. Beschrijf wat ZICHTBAAR is (textuur, glans, roodheid, poriën). Geen ziektenamen. Zachte adviezen: hydratatie, SPF, slaap. Alarm: plotse opflakkering, pijn, uitbreidende laesies → dermatoloog.",
  },
  steps: [
    s("d0",  0,  { fr: "J0 · Baseline",  en: "D0 · Baseline",  nl: "D0 · Baseline" },
      { fr: ["Selfie face en lumière naturelle (fenêtre, sans flash)", "Note ta routine actuelle (matin/soir)"],
        en: ["Front selfie in natural daylight (window, no flash)", "Note your current routine (AM/PM)"],
        nl: ["Selfie front in daglicht (raam, geen flits)", "Noteer je huidige routine (ochtend/avond)"] }),
    s("d3",  3,  { fr: "J3", en: "D3", nl: "D3" },
      { fr: ["Selfie (même cadrage)", "1,5 L d'eau aujourd'hui"], en: ["Selfie (same framing)", "1.5 L water today"], nl: ["Selfie (zelfde kader)", "1,5 L water vandaag"] }),
    s("d6",  6,  { fr: "J6", en: "D6", nl: "D6" },
      { fr: ["Selfie", "SPF 30+ si exposition"], en: ["Selfie", "SPF 30+ if sun exposure"], nl: ["Selfie", "SPF 30+ bij zon"] }),
    s("d9",  9,  { fr: "J9", en: "D9", nl: "D9" },
      { fr: ["Selfie", "Note tolérance des actifs"], en: ["Selfie", "Note actives tolerance"], nl: ["Selfie", "Tolerantie actieve stoffen"] }),
    s("d12", 12, { fr: "J12", en: "D12", nl: "D12" },
      { fr: ["Selfie", "Sommeil ≥ 7 h"], en: ["Selfie", "Sleep ≥ 7 h"], nl: ["Selfie", "Slaap ≥ 7 u"] }),
    s("d15", 15, { fr: "J15 · Mi-parcours", en: "D15 · Mid", nl: "D15 · Tussentijds" },
      { fr: ["Selfie", "Comparer à J0 dans l'historique"], en: ["Selfie", "Compare to D0 in history"], nl: ["Selfie", "Vergelijk met D0"] }),
    s("d18", 18, { fr: "J18", en: "D18", nl: "D18" },
      { fr: ["Selfie", "Aliments anti-inflammatoires (poisson, légumes verts)"], en: ["Selfie", "Anti-inflammatory foods (fish, greens)"], nl: ["Selfie", "Ontstekingsremmende voeding"] }),
    s("d21", 21, { fr: "J21", en: "D21", nl: "D21" },
      { fr: ["Selfie", "Sérum/crème : 1 seul nouveau produit à la fois"], en: ["Selfie", "Only introduce 1 new product at a time"], nl: ["Selfie", "Slechts 1 nieuw product per keer"] }),
    s("d24", 24, { fr: "J24", en: "D24", nl: "D24" },
      { fr: ["Selfie", "Évite eau trop chaude au nettoyage"], en: ["Selfie", "Avoid very hot water when cleansing"], nl: ["Selfie", "Vermijd te heet water bij reiniging"] }),
    s("d27", 27, { fr: "J27", en: "D27", nl: "D27" },
      { fr: ["Selfie", "Vérifie SPF quotidien"], en: ["Selfie", "Daily SPF check"], nl: ["Selfie", "Dagelijkse SPF"] }),
    s("d30", 30, { fr: "J30 · Bilan", en: "D30 · Wrap-up", nl: "D30 · Eindbalans" },
      { fr: ["Selfie", "Rapport PDF avant/après"], en: ["Selfie", "Before/after PDF report"], nl: ["Selfie", "Voor/na PDF-rapport"] }),
  ],
  extension: {
    label: { fr: "Prolonger jusqu'à J60", en: "Extend to D60", nl: "Verleng tot D60" },
    rationale: {
      fr: "Le renouvellement cutané prend ~28 j : prolonger 30 j de plus permet de voir s'installer durablement les effets de la routine.",
      en: "Skin renewal takes ~28 days: another 30 days lets the routine's effects settle in.",
      nl: "Huidvernieuwing duurt ~28 dagen: 30 extra dagen laten de effecten goed bezinken.",
    },
    steps: [
      s("d36", 36, { fr: "J36", en: "D36", nl: "D36" }, { fr: ["Selfie"], en: ["Selfie"], nl: ["Selfie"] }),
      s("d45", 45, { fr: "J45", en: "D45", nl: "D45" }, { fr: ["Selfie"], en: ["Selfie"], nl: ["Selfie"] }),
      s("d60", 60, { fr: "J60 · Bilan étendu", en: "D60 · Extended wrap-up", nl: "D60 · Uitgebreide balans" },
        { fr: ["Selfie", "Rapport PDF complet"], en: ["Selfie", "Full PDF report"], nl: ["Selfie", "Volledig PDF-rapport"] }),
    ],
  },
};

// --------------------------- ANTIAGING ---------------------------
const ANTIAGING: ProtocolDef = {
  goal: "antiaging",
  tier: "core",
  title: { fr: "Anti-âge · 30 jours", en: "Anti-ageing · 30 days", nl: "Anti-veroudering · 30 dagen" },
  subtitle: {
    fr: "Une photo par semaine — fermeté, ridules, repos visible.",
    en: "One photo per week — firmness, fine lines, rested look.",
    nl: "Eén foto per week — stevigheid, fijne lijntjes, uitgeruste look.",
  },
  aiContext: {
    fr: "Suivi non médical anti-âge. Décris la peau (fermeté apparente, ridules, hydratation, cernes). Aucune promesse anti-rides. Conseils : sommeil, SPF quotidien, rétinoïdes cosmétiques (si tolérés), hydratation, hygiène de vie. Pas de prescription, pas de nom de pathologie.",
    en: "Non-medical anti-ageing tracking. Describe firmness, fine lines, hydration, eye-bags. No anti-wrinkle promises. Advice: sleep, daily SPF, cosmetic retinoids (if tolerated), hydration. No prescriptions, no disease names.",
    nl: "Niet-medische anti-veroudering. Beschrijf stevigheid, lijntjes, hydratatie, wallen. Geen anti-rimpel beloften. Advies: slaap, dagelijkse SPF, cosmetische retinoïden (indien verdragen).",
  },
  steps: [
    s("w0",  0,  { fr: "S0 · Baseline", en: "W0 · Baseline", nl: "W0 · Baseline" },
      { fr: ["Selfie face + profil léger", "Note tes habitudes de sommeil"], en: ["Front + slight-profile selfies", "Note sleep habits"], nl: ["Front + lichte profielselfie", "Slaapgewoonten"] }),
    s("w1",  7,  { fr: "S1", en: "W1", nl: "W1" }, { fr: ["Selfie", "SPF tous les jours cette semaine"], en: ["Selfie", "Daily SPF this week"], nl: ["Selfie", "Dagelijkse SPF"] }),
    s("w2",  14, { fr: "S2", en: "W2", nl: "W2" }, { fr: ["Selfie", "Sommeil ≥ 7 h × 5 nuits"], en: ["Selfie", "Sleep ≥ 7 h on 5 nights"], nl: ["Selfie", "Slaap ≥ 7 u op 5 nachten"] }),
    s("w3",  21, { fr: "S3", en: "W3", nl: "W3" }, { fr: ["Selfie", "Hydratation + oméga-3"], en: ["Selfie", "Hydration + omega-3"], nl: ["Selfie", "Hydratatie + omega-3"] }),
    s("w4",  30, { fr: "S4 · Bilan", en: "W4 · Wrap-up", nl: "W4 · Eindbalans" }, { fr: ["Selfie", "Rapport PDF avant/après"], en: ["Selfie", "Before/after PDF"], nl: ["Selfie", "Voor/na PDF"] }),
  ],
  extension: {
    label: { fr: "Prolonger jusqu'à M3", en: "Extend to M3", nl: "Verleng tot M3" },
    rationale: {
      fr: "Les effets anti-âge visibles (collagène, élasticité) se mesurent surtout sur 8–12 semaines.",
      en: "Visible anti-ageing effects (collagen, elasticity) are best measured over 8–12 weeks.",
      nl: "Zichtbare anti-verouderingseffecten zijn het best meetbaar over 8–12 weken.",
    },
    steps: [
      s("m2", 60, { fr: "M+2", en: "M+2", nl: "M+2" }, { fr: ["Selfie"], en: ["Selfie"], nl: ["Selfie"] }),
      s("m3", 90, { fr: "M+3 · Bilan étendu", en: "M+3 · Extended wrap-up", nl: "M+3 · Uitgebreide balans" }, { fr: ["Selfie", "Rapport PDF"], en: ["Selfie", "PDF report"], nl: ["Selfie", "PDF-rapport"] }),
    ],
  },
};

// ----------------------------- WEIGHT -----------------------------
const WEIGHT: ProtocolDef = {
  goal: "weight",
  tier: "core",
  title: { fr: "Forme & Vitalité · 30 jours", en: "Fitness & Vitality · 30 days", nl: "Fitheid & Vitaliteit · 30 dagen" },
  subtitle: {
    fr: "Une photo corps entier par semaine — silhouette, posture, énergie.",
    en: "One full-body photo per week — silhouette, posture, energy.",
    nl: "Eén foto volledig lichaam per week — silhouet, houding, energie.",
  },
  aiContext: {
    fr: "Suivi non médical de forme physique. Décris la silhouette VISIBLE (posture, tonus apparent). Aucune mention de poids cible ni de stigmatisation. Conseils : marche 7–10k pas, protéines 1,2–1,6 g/kg, sommeil, hydratation. Pas de régime restrictif, pas de prescription.",
    en: "Non-medical fitness tracking. Describe VISIBLE silhouette (posture, apparent tone). No target weight, no body shaming. Advice: 7–10k steps, protein 1.2–1.6 g/kg, sleep, hydration. No restrictive diets, no prescriptions.",
    nl: "Niet-medische fitheidsopvolging. Beschrijf zichtbaar silhouet (houding, tonus). Geen streefgewicht, geen body shaming. Advies: 7–10k stappen, eiwitten 1,2–1,6 g/kg, slaap, hydratatie.",
  },
  steps: [
    s("w0", 0,  { fr: "S0 · Baseline", en: "W0 · Baseline", nl: "W0 · Baseline" },
      { fr: ["Photo face + profil (corps entier, vêtement ajusté)", "Note tes objectifs (énergie/posture)"],
        en: ["Front + side full-body photo (snug clothing)", "Note your goals (energy/posture)"],
        nl: ["Front + zijfoto (volledig lichaam, strakke kleding)", "Noteer doelen"] },
      "full_body",
      { fr: "Même endroit, même heure, même éclairage à chaque fois", en: "Same spot, same time, same lighting each time", nl: "Telkens zelfde plek, tijd, licht" }),
    s("w1", 7,  { fr: "S1", en: "W1", nl: "W1" }, { fr: ["Photo (même cadrage)", "5 séances actives cette semaine"], en: ["Photo (same framing)", "5 active sessions this week"], nl: ["Foto (zelfde kader)", "5 actieve sessies deze week"] }, "full_body"),
    s("w2", 14, { fr: "S2", en: "W2", nl: "W2" }, { fr: ["Photo", "Protéines à chaque repas"], en: ["Photo", "Protein at every meal"], nl: ["Foto", "Eiwitten bij elke maaltijd"] }, "full_body"),
    s("w3", 21, { fr: "S3", en: "W3", nl: "W3" }, { fr: ["Photo", "Sommeil ≥ 7 h"], en: ["Photo", "Sleep ≥ 7 h"], nl: ["Foto", "Slaap ≥ 7 u"] }, "full_body"),
    s("w4", 30, { fr: "S4 · Bilan", en: "W4 · Wrap-up", nl: "W4 · Eindbalans" }, { fr: ["Photo", "Rapport PDF avant/après"], en: ["Photo", "Before/after PDF"], nl: ["Foto", "Voor/na PDF"] }, "full_body"),
  ],
  extension: {
    label: { fr: "Prolonger jusqu'à S12", en: "Extend to W12", nl: "Verleng tot W12" },
    rationale: {
      fr: "Les changements de composition corporelle deviennent vraiment visibles sur 8–12 semaines.",
      en: "Body-composition changes become truly visible over 8–12 weeks.",
      nl: "Veranderingen in lichaamssamenstelling worden pas echt zichtbaar na 8–12 weken.",
    },
    steps: [
      s("w6",  42, { fr: "S6",  en: "W6",  nl: "W6"  }, { fr: ["Photo"], en: ["Photo"], nl: ["Foto"] }, "full_body"),
      s("w8",  56, { fr: "S8",  en: "W8",  nl: "W8"  }, { fr: ["Photo"], en: ["Photo"], nl: ["Foto"] }, "full_body"),
      s("w12", 84, { fr: "S12 · Bilan étendu", en: "W12 · Extended wrap-up", nl: "W12 · Uitgebreide balans" }, { fr: ["Photo", "Rapport PDF"], en: ["Photo", "PDF report"], nl: ["Foto", "PDF-rapport"] }, "full_body"),
    ],
  },
};

// ----------------------------- MUSCLE -----------------------------
const MUSCLE: ProtocolDef = {
  goal: "muscle",
  tier: "core",
  title: { fr: "Tonus musculaire · 30 jours", en: "Muscle tone · 30 days", nl: "Spiertonus · 30 dagen" },
  subtitle: {
    fr: "Une photo par semaine, 3 angles — posture, symétrie, tonus apparent.",
    en: "One photo per week, 3 angles — posture, symmetry, apparent tone.",
    nl: "Eén foto per week, 3 hoeken — houding, symmetrie, tonus.",
  },
  aiContext: {
    fr: "Suivi non médical du tonus musculaire. Décris la posture et le tonus VISIBLE (épaules, dos, ventre). Aucune prescription d'entraînement médical. Conseils : musculation 3×/sem, mobilité, protéines, récupération. Pas de stigmatisation, pas de comparaison à un standard.",
    en: "Non-medical muscle-tone tracking. Describe VISIBLE posture and tone (shoulders, back, core). No medical training prescriptions. Advice: resistance 3×/wk, mobility, protein, recovery. No body shaming.",
    nl: "Niet-medische opvolging spiertonus. Beschrijf zichtbare houding en tonus. Advies: krachttraining 3×/wk, mobiliteit, eiwitten, herstel. Geen body shaming.",
  },
  steps: [
    s("w0", 0,  { fr: "S0 · Baseline 3 angles", en: "W0 · Baseline 3 angles", nl: "W0 · Baseline 3 hoeken" },
      { fr: ["Photo face, profil, dos (vêtement ajusté)", "Note tes séances types"],
        en: ["Front, side, back photos (snug clothing)", "Note your typical sessions"],
        nl: ["Front, zij, rug (strakke kleding)", "Noteer je sessies"] }, "full_body"),
    s("w1", 7,  { fr: "S1", en: "W1", nl: "W1" }, { fr: ["Photo", "3 séances de renforcement"], en: ["Photo", "3 resistance sessions"], nl: ["Foto", "3 krachtsessies"] }, "full_body"),
    s("w2", 14, { fr: "S2", en: "W2", nl: "W2" }, { fr: ["Photo", "Mobilité 10 min × 3"], en: ["Photo", "Mobility 10 min × 3"], nl: ["Foto", "Mobiliteit 10 min × 3"] }, "full_body"),
    s("w3", 21, { fr: "S3", en: "W3", nl: "W3" }, { fr: ["Photo", "Augmente progressivement les charges"], en: ["Photo", "Progress the loads gradually"], nl: ["Foto", "Verhoog belasting geleidelijk"] }, "full_body"),
    s("w4", 30, { fr: "S4 · Bilan", en: "W4 · Wrap-up", nl: "W4 · Eindbalans" }, { fr: ["Photo", "Rapport PDF avant/après"], en: ["Photo", "Before/after PDF"], nl: ["Foto", "Voor/na PDF"] }, "full_body"),
  ],
  extension: {
    label: { fr: "Prolonger jusqu'à S12", en: "Extend to W12", nl: "Verleng tot W12" },
    rationale: {
      fr: "L'hypertrophie visible demande 8–12 semaines de stimulus régulier.",
      en: "Visible hypertrophy requires 8–12 weeks of consistent stimulus.",
      nl: "Zichtbare hypertrofie vraagt 8–12 weken consistente prikkel.",
    },
    steps: [
      s("w6",  42, { fr: "S6",  en: "W6",  nl: "W6"  }, { fr: ["Photo"], en: ["Photo"], nl: ["Foto"] }, "full_body"),
      s("w8",  56, { fr: "S8",  en: "W8",  nl: "W8"  }, { fr: ["Photo"], en: ["Photo"], nl: ["Foto"] }, "full_body"),
      s("w12", 84, { fr: "S12 · Bilan étendu", en: "W12 · Extended wrap-up", nl: "W12 · Uitgebreide balans" }, { fr: ["Photo", "Rapport PDF"], en: ["Photo", "PDF report"], nl: ["Foto", "PDF-rapport"] }, "full_body"),
    ],
  },
};

export const BASE_PROTOCOLS: ProtocolDef[] = [SKIN, ANTIAGING, WEIGHT, MUSCLE];